/*
Common core of algolia functionality used by
both the use-algolia Hook, and the Next JS
GetServerSideProps for server rendering
*/

import algoliasearch from 'algoliasearch'
import { Centre } from '../../../types/contentful-v1'
import { getNowInCentreInTimeFormat } from '../../helpers/date-helpers'
import { AlgoliaApiKeys, Segment, DoQueryAlgoliaArgs, Clients } from './types'
import querySegments from './query-segments'

interface OffsetlessTimeGetters {
  getOffsetlessTimeInDefaultTz: () => number
  getOffsetlessTimeInCentre?: () => number
}

const getDefaultNationalTimezone = (country: string): string => {
  if (country === 'New Zealand') {
    return 'Pacific/Auckland'
  } else {
    return 'Australia/Sydney'
  }
}

export const makeOffsetlessTimeGetters = (
  country: string,
  centre?: Centre
): OffsetlessTimeGetters => ({
  getOffsetlessTimeInDefaultTz: getNowInCentreInTimeFormat(
    getDefaultNationalTimezone(country)
  ) as () => number,
  getOffsetlessTimeInCentre:
    centre && (getNowInCentreInTimeFormat(centre.timeZone) as () => number),
})

export function makeClients(
  algoliaApiKeys: AlgoliaApiKeys
): Clients | undefined {
  const {
    contentApplicationId,
    contentApiKey,
    marketplaceApplicationId,
    marketplaceApiKey,
  } = algoliaApiKeys
  if (
    !contentApplicationId ||
    !contentApiKey ||
    !marketplaceApplicationId ||
    !marketplaceApiKey
  ) {
    return undefined
  }
  return {
    content: algoliasearch(contentApplicationId, contentApiKey),
    marketplace: algoliasearch(marketplaceApplicationId, marketplaceApiKey),
  }
}

const segments = Object.values(Segment)

export const doQueryAlgolia = ({
  query,
  clients,
  context,
}: DoQueryAlgoliaArgs): ReturnType<typeof querySegments> =>
  clients
    ? querySegments(query, clients, context, segments)
    : Promise.reject(new Error('Algolia clients not initalised'))
